/**
 * Volume Discount Display
 *
 * Powers all 3 display styles (table, progress, badge) on the product page,
 * plus the cart banner upsell messaging.
 *
 * Reads tier data from inline JSON or data attributes set by Liquid snippets.
 * Listens to the quantity input on the product form and updates UI dynamically.
 *
 * Does NOT interact with _incentivized, _bundle_id, or any bundle logic.
 */

(function () {
  'use strict';

  // ─── Tier Utilities ───────────────────────────────────────────────

  /**
   * Find the matching tier for a given quantity.
   * @param {Array<{min:number, max:number|null, percent:number}>} tiers
   * @param {number} qty
   * @returns {{min:number, max:number|null, percent:number}|null}
   */
  function findTier(tiers, qty) {
    let matched = null;
    for (const tier of tiers) {
      if (qty >= tier.min) {
        if (tier.max === null || tier.max === undefined || qty <= tier.max) {
          if (!matched || tier.min > matched.min) {
            matched = tier;
          }
        }
      }
    }
    return matched;
  }

  /**
   * Find the next tier the customer could unlock.
   * @param {Array<{min:number, max:number|null, percent:number}>} tiers
   * @param {number} qty
   * @returns {{min:number, max:number|null, percent:number}|null}
   */
  function findNextTier(tiers, qty) {
    const sorted = [...tiers].sort((a, b) => a.min - b.min);
    for (const tier of sorted) {
      if (qty < tier.min) return tier;
    }
    return null;
  }

  /**
   * Format a price in cents to currency string.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  // ─── Set Quantity Input ───────────────────────────────────────────

  /**
   * Programmatically set the product-form quantity input to a given value
   * and fire both 'input' and 'change' events so the theme and our polling
   * will both react.
   * @param {number} qty
   */
  function setQuantityInput(qty) {
    function getDynamicQtyInput(container) {
      // Use scoped container if provided, otherwise fallback to standard logic
      var scope = container || document;
      
      var vdEl = scope.querySelector('[data-volume-discount-display]');
      var section = vdEl ? (vdEl.closest('.product__info-wrapper') ||
        vdEl.closest('[id^="MainProduct"]') ||
        vdEl.closest('.gqv-layout') ||
        vdEl.closest('section') ||
        vdEl.closest('form')) : scope;

      return section.querySelector('quantity-input input') ||
        section.querySelector('.quantity__input') ||
        section.querySelector('input[name="quantity"]') ||
        document.querySelector('.gqv-form input[name="quantity"]') ||
        document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    }

    var qtyInput = getDynamicQtyInput();
    if (!qtyInput) return;
    qtyInput.value = qty;
    
    // Support for Quick View's custom stepper UI
    var section = qtyInput.closest('.gqv-layout') || qtyInput.closest('form');
    if (section) {
      var visualVal = section.querySelector('.gqv-stepper-val');
      if (visualVal) visualVal.textContent = qty;
    }

    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ─── Table Style ──────────────────────────────────────────────────

  function buildTableRows(container) {
    var tiersRaw = container.dataset.tiers;
    if (!tiersRaw) return;
    var tiers;
    try { tiers = JSON.parse(tiersRaw); } catch { return; }

    var tbody = container.querySelector('[data-volume-table-body]');
    if (!tbody) return;

    var basePrice = parseInt(container.dataset.basePrice, 10) || 0;

    // Remove previously injected rows (keep only the first static row)
    var existing = tbody.querySelectorAll('[data-tier-injected]');
    existing.forEach(function (r) { r.remove(); });

    tiers.forEach(function (tier) {
      var priceEach = basePrice * (1 - tier.percent / 100);
      var savingsEach = basePrice - priceEach;
      var label = tier.max ? (tier.min + '–' + tier.max) : (tier.min + '+');
      var tr = document.createElement('tr');
      tr.className = 'volume-discount-table__row';
      tr.dataset.tierMin = tier.min;
      tr.dataset.tierMax = tier.max || '';
      tr.dataset.tierInjected = '1';
      tr.innerHTML =
        '<td>' + label + '</td>' +
        '<td>' + formatMoney(Math.round(priceEach)) + '</td>' +
        '<td>' + tier.percent + '% (' + formatMoney(Math.round(savingsEach)) + ')</td>';
      tbody.appendChild(tr);
    });
  }

  function initTableStyle(container, qty) {
    var rows = container.querySelectorAll('.volume-discount-table__row');
    rows.forEach(function (row) {
      var min = parseInt(row.dataset.tierMin, 10);
      var max = row.dataset.tierMax ? parseInt(row.dataset.tierMax, 10) : Infinity;
      var isActive = qty >= min && qty <= max;
      row.classList.toggle('is-active', isActive);
    });
  }

  // ─── Progress Style ───────────────────────────────────────────────

  function initProgressStyle(container, qty) {
    const tiersRaw = container.dataset.tiers;
    if (!tiersRaw) return;

    let tiers;
    try {
      tiers = JSON.parse(tiersRaw);
    } catch {
      return;
    }

    const bar = container.querySelector('[data-volume-progress-bar]');
    const status = container.querySelector('[data-volume-progress-status]');
    const savings = container.querySelector('[data-volume-progress-savings]');
    if (!bar || !status) return;

    const maxMin = Math.max.apply(null, tiers.map(function (t) { return t.min; })) || 10;
    const currentTier = findTier(tiers, qty);
    const nextTier = findNextTier(tiers, qty);

    // Progress bar: percentage toward the highest tier
    const progress = Math.min((qty / maxMin) * 100, 100);
    bar.style.width = progress + '%';

    // Status message
    if (currentTier && !nextTier) {
      status.innerHTML = '<span class="unlocked">' + currentTier.percent + '% discount — max savings!</span>';
    } else if (currentTier && nextTier) {
      const diff = nextTier.min - qty;
      status.innerHTML =
        '<span class="unlocked">' + currentTier.percent + '% discount unlocked!</span>' +
        ' Add ' + diff + ' more for ' + nextTier.percent + '% off';
    } else if (nextTier) {
      const diff = nextTier.min - qty;
      status.textContent = 'Add ' + diff + ' more for ' + nextTier.percent + '% off';
    } else {
      status.textContent = 'Select quantity to see discounts';
    }

    // Savings display
    if (savings && currentTier) {
      const basePrice = getBasePrice();
      if (basePrice > 0) {
        const saved = Math.round(basePrice * qty * currentTier.percent / 100);
        savings.textContent = 'You save: ' + formatMoney(saved);
      }
    } else if (savings) {
      savings.textContent = '';
    }
  }

  // ─── Packs Style ─────────────────────────────────────────────────

  /**
   * Build and/or update the pack-selector cards.
   * Cards are built once the first time (data-vd-packs-built), then only
   * the active state + savings summary are updated on subsequent calls.
   * @param {HTMLElement} container
   * @param {number} qty
   */
  function initPacksStyle(container, qty) {
    var tiersRaw = container.dataset.tiers;
    if (!tiersRaw) return;
    var tiers;
    try { tiers = JSON.parse(tiersRaw); } catch { return; }

    var basePrice = parseInt(container.dataset.basePrice, 10) || 0;
    var grid = container.querySelector('[data-vd-packs-grid]');
    var summary = container.querySelector('[data-vd-packs-summary]');
    if (!grid) return;

    // ── Build cards once ──
    if (!container.dataset.vdPacksBuilt) {
      container.dataset.vdPacksBuilt = '1';

      // Sort tiers ascending
      var sorted = tiers.slice().sort(function (a, b) { return a.min - b.min; });
      var maxTierIndex = sorted.length - 1;

      // "1 Unit" base card
      var baseCard = document.createElement('div');
      baseCard.className = 'vd-pack-card is-base-unit';
      baseCard.dataset.vdPackQty = '1';
      baseCard.innerHTML =
        '<span class="vd-pack-card__name">1 Unit</span>' +
        '<div class="vd-pack-card__unit-price">' +
        '<span class="vd-pack-card__price-discounted">' + formatMoney(basePrice) + '</span>' +
        '</div>' +
        '<span class="vd-pack-card__each">each</span>';
      baseCard.addEventListener('click', function () {
        setQuantityInput(1);
        updateAllDisplays(1);
      });
      grid.appendChild(baseCard);

      // Tier cards
      sorted.forEach(function (tier, idx) {
        var packQty = tier.min;
        var discountedUnit = Math.round(basePrice * (1 - tier.percent / 100));
        var totalPack = discountedUnit * packQty;
        var totalSavings = (basePrice * packQty) - totalPack;
        var isBest = idx === maxTierIndex;

        var card = document.createElement('div');
        card.className = 'vd-pack-card';
        if (isBest) card.classList.add('is-best-value');

        card.dataset.vdPackQty = String(packQty);
        card.dataset.vdTierMin = String(tier.min);
        card.dataset.vdTierMax = tier.max != null ? String(tier.max) : '';

        var badgeHtml = '';
        if (isBest) {
          badgeHtml = '<span class="vd-pack-card__badge is-best">Best Value</span>';
        } else {
          badgeHtml = '<span class="vd-pack-card__badge">' + tier.percent + '% off</span>';
        }

        var nameLabel = packQty + ' Pack';
        if (packQty === 1) nameLabel = '1 Unit';

        var savingsHtml = '';
        if (packQty > 1) {
          savingsHtml = '<span class="vd-pack-card__savings-amt">Save ' + formatMoney(totalSavings) + '</span>';
        }

        card.innerHTML =
          badgeHtml +
          '<span class="vd-pack-card__name">' + nameLabel + '</span>' +
          '<div class="vd-pack-card__pricing">' +
          '<span class="vd-pack-card__price-discounted">' + formatMoney(discountedUnit) + ' <small>each</small></span>' +
          savingsHtml +
          '</div>';

        card.addEventListener('click', function () {
          setQuantityInput(packQty);
          updateAllDisplays(packQty);
        });

        grid.appendChild(card);
      });
    }

    // ── Update active state ──
    var currentTier = findTier(tiers, qty);

    grid.querySelectorAll('.vd-pack-card').forEach(function (card) {
      var cardMin = parseInt(card.dataset.vdTierMin, 10);
      var cardMax = card.dataset.vdTierMax ? parseInt(card.dataset.vdTierMax, 10) : Infinity;
      var isBase = card.classList.contains('is-base-unit');

      var isActive;
      if (isBase) {
        isActive = !currentTier;
      } else {
        isActive = qty >= cardMin && qty <= cardMax;
      }
      card.classList.toggle('is-active', isActive);
    });

  }

  // ─── Badge Style ──────────────────────────────────────────────────

  function initBadgeStyle(container, qty) {
    const tiersRaw = container.dataset.tiers;
    if (!tiersRaw) return;

    let tiers;
    try {
      tiers = JSON.parse(tiersRaw);
    } catch {
      return;
    }

    const tag = container.querySelector('[data-volume-badge-tag]');
    const percentEl = container.querySelector('[data-volume-badge-percent]');
    const hint = container.querySelector('[data-volume-badge-hint]');
    if (!tag || !percentEl || !hint) return;

    const currentTier = findTier(tiers, qty);
    const nextTier = findNextTier(tiers, qty);

    if (currentTier) {
      tag.style.display = '';
      percentEl.textContent = Math.round(currentTier.percent);
      hint.classList.add('is-hidden');
    } else {
      tag.style.display = 'none';
      hint.classList.remove('is-hidden');
      if (nextTier) {
        const diff = nextTier.min - qty;
        hint.textContent = 'Buy ' + diff + ' more for ' + nextTier.percent + '% off';
      }
    }
  }

  // ─── Base Price Helper ────────────────────────────────────────────

  function getBasePrice() {
    // Try reading from the badge data attribute first
    const badge = document.querySelector('[data-volume-discount-display="badge"]');
    if (badge && badge.dataset.basePrice) {
      return parseInt(badge.dataset.basePrice, 10);
    }
    // Fallback: try to read from product JSON
    const productJson = document.querySelector('[data-volume-product-json]');
    if (productJson) {
      try {
        const data = JSON.parse(productJson.textContent);
        return data.price || 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }

  // ─── Buy Button Price ────────────────────────────────────────────

  /**
   * Update the Add to Cart button with the total discounted price.
   * @param {number} qty
   */
  function updateBuyButton(qty, container) {
    try {
      // Prioritize scoped container
      const scope = container || document;
      
      // Quick View specific total span
      var ctaPrice = scope.querySelector('.gqv-add-total');
      // PDP specific price span (from Dawn or custom)
      if (!ctaPrice) {
        ctaPrice = scope.querySelector('.product-form__submit .submit-price') || 
                   scope.querySelector('[id^="cta-price-display"]');
      }
      
      if (!ctaPrice) return;

      var basePrice = parseFloat(ctaPrice.dataset.basePrice) || 0;
      var vdContainer = scope.querySelector('[data-volume-discount-display]');

      var unitPrice = basePrice;

      if (vdContainer && vdContainer.dataset.tiers) {
        try {
          var tiers = JSON.parse(vdContainer.dataset.tiers);
          var tier = findTier(tiers, qty);
          if (tier) {
            unitPrice = Math.round(basePrice * (1 - tier.percent / 100));
          }
        } catch (e) { }
      }

      var totalPrice = unitPrice * qty;
      
      // If it's the Quick View total span, we usually have a dash/separator
      if (ctaPrice.classList.contains('gqv-add-total')) {
        ctaPrice.textContent = formatMoney(totalPrice);
      } else {
        ctaPrice.textContent = formatMoney(totalPrice);
      }
    } catch (err) {
      console.error('VD: Buy button update fail', err);
    }
  }

  // ─── Main Update Function ─────────────────────────────────────────

  function updateAllDisplays(qty, container) {
    const scope = container || document;
    
    // Table style
    scope.querySelectorAll('[data-volume-discount-display="table"]').forEach((el) => {
      buildTableRows(el);
      initTableStyle(el, qty);
    });

    // Progress style
    scope.querySelectorAll('[data-volume-discount-display="progress"]').forEach((el) => {
      initProgressStyle(el, qty);
    });

    // Badge style
    scope.querySelectorAll('[data-volume-discount-display="badge"]').forEach((el) => {
      initBadgeStyle(el, qty);
    });

    // Packs style
    scope.querySelectorAll('[data-volume-discount-display="packs"]').forEach((el) => {
      initPacksStyle(el, qty);
    });

    // Update Buy Button Price
    updateBuyButton(qty, scope);
  }

  // ─── Cart Banner ──────────────────────────────────────────────────

  function updateCartBanner() {
    const banner = document.querySelector('[data-volume-cart-banner]');
    if (!banner) return;

    const messageEl = banner.querySelector('[data-volume-cart-message]');
    const ctaEl = banner.querySelector('[data-volume-cart-cta]');
    if (!messageEl || !ctaEl) return;

    // Read cart tier data embedded by Liquid
    const cartDataEl = document.querySelector('[data-volume-cart-tiers]');
    if (!cartDataEl) {
      banner.style.display = 'none';
      return;
    }

    let cartItems;
    try {
      cartItems = JSON.parse(cartDataEl.textContent);
    } catch {
      banner.style.display = 'none';
      return;
    }

    // Find the best upsell opportunity
    let bestUpsell = null;
    let hasSavings = false;

    for (const item of cartItems) {
      if (!item.tiers || item.tiers.length === 0) continue;

      const currentTier = findTier(item.tiers, item.quantity);
      const nextTier = findNextTier(item.tiers, item.quantity);

      if (currentTier) {
        hasSavings = true;
        if (nextTier) {
          const diff = nextTier.min - item.quantity;
          if (!bestUpsell || diff < bestUpsell.diff) {
            bestUpsell = {
              title: item.title,
              diff: diff,
              nextPercent: nextTier.percent,
              currentPercent: currentTier.percent,
            };
          }
        }
      } else if (nextTier) {
        const diff = nextTier.min - item.quantity;
        if (!bestUpsell || diff < bestUpsell.diff) {
          bestUpsell = {
            title: item.title,
            diff: diff,
            nextPercent: nextTier.percent,
            currentPercent: 0,
          };
        }
      }
    }

    if (bestUpsell) {
      banner.style.display = '';
      if (bestUpsell.currentPercent > 0) {
        messageEl.textContent = 'You\'re saving with ' + bestUpsell.currentPercent + '% volume discount!';
      } else {
        messageEl.textContent = 'Volume discounts available!';
      }
      ctaEl.textContent =
        'Add ' + bestUpsell.diff + ' more ' + bestUpsell.title + ' for ' + bestUpsell.nextPercent + '% off →';
    } else if (hasSavings) {
      banner.style.display = '';
      messageEl.textContent = 'You\'re saving with max volume discount!';
      ctaEl.textContent = '';
    } else {
      banner.style.display = 'none';
    }
  }

  // ─── Event Listeners ──────────────────────────────────────────────

  function init() {
    var lastQty = 1;

    function getDynamicQtyInput() {
      var vdEl = document.querySelector('[data-volume-discount-display]');
      var section = vdEl ? (vdEl.closest('.product__info-wrapper') ||
        vdEl.closest('[id^="MainProduct"]') ||
        vdEl.closest('section') ||
        vdEl.closest('form')) : document;

      return section.querySelector('quantity-input input') ||
        section.querySelector('.quantity__input') ||
        section.querySelector('input[name="quantity"]') ||
        document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    }

    // Initial check
    var initialInput = getDynamicQtyInput();
    if (initialInput) {
      lastQty = parseInt(initialInput.value, 10) || 1;
      updateAllDisplays(lastQty);
    }

    // Delegate "change" and "input" to handle dynamic replacements
    document.addEventListener('change', function (e) {
      if (e.target.matches('input[name="quantity"]') || e.target.classList.contains('quantity__input')) {
        var q = parseInt(e.target.value, 10) || 1;
        lastQty = q;
        updateAllDisplays(q);
      }
    }, true);

    document.addEventListener('input', function (e) {
      if (e.target.matches('input[name="quantity"]') || e.target.classList.contains('quantity__input')) {
        var q = parseInt(e.target.value, 10) || 1;
        lastQty = q;
        updateAllDisplays(q);
      }
    }, true);

    // Poller to catchDawn theme button clicks (which don't always fire events)
    setInterval(function () {
      var qtyInput = getDynamicQtyInput();
      if (!qtyInput) return;
      var q = parseInt(qtyInput.value, 10) || 1;
      if (q !== lastQty) {
        lastQty = q;
        updateAllDisplays(q);
      }
    }, 250);

    // Cart banner
    updateCartBanner();
    document.addEventListener('cart:updated', updateCartBanner);

    // Listen for Quick View loads
    window.addEventListener('gqv:loaded', function(e) {
      const gqvContainer = e.detail.container;
      if (!gqvContainer) return;

      const gqvInput = getDynamicQtyInput(gqvContainer);
      if (gqvInput) {
        const q = parseInt(gqvInput.value, 10) || 1;
        updateAllDisplays(q, gqvContainer);
      }
    });

    // Listen for variant CHANGES to refresh volume pricing base-price
    document.addEventListener('variantChange', function(e) {
      const section = document.querySelector(`[data-section="${e.detail.sectionId}"]`) || 
                      document.getElementById(`MainProduct-${e.detail.sectionId}`) ||
                      document.querySelector(`.gqv-layout`); // Quick view fallback
      
      if (!section) return;

      // Small timeout to ensure DOM update (updateSourceFromDestination) is completed
      setTimeout(() => {
        const qtyInput = getDynamicQtyInput(section);
        const q = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        updateAllDisplays(q, section);
      }, 50);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support for Shopify Theme Editor (Customizer)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.matches('.volume-discount-container') || e.target.querySelector('[data-volume-discount-display]')) {
      init();
    }
  });
})();
